import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { FrontendLoggerService } from '../frontend-logger.service';
import {
  GenerateDocumentApiRequest,
  GenerateDocumentRequest,
  GenerateEnqueueResponse,
  GeneratedSummaryDetail,
  GeneratedSummaryLifecycleStatus,
  GeneratedSummaryListItem,
  GeneratedSummaryListResponse,
  SummaryLifecycleActionResponse,
  SummaryLifecycleTransition
} from 'src/app/models/generation/generation.model';
import { DownloadedFile } from './download.util';

@Injectable({ providedIn: 'root' })
export class GenerationService {
  constructor(
    private readonly api: ApiService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  generateExecutiveSummary(request: GenerateDocumentRequest): Observable<GenerateEnqueueResponse> {
    const payload: GenerateDocumentApiRequest = {
      type: 'executive_summary',
      source_refs: request.sourceRefs,
      language: request.language || 'es',
      expert_role: request.expertRole || null,
    };

    const message = request.message?.trim();
    if (message) {
      payload.message = message;
    }

    // Async: el backend responde 202 con { taskId, status } y genera en segundo plano.
    this.frontendLogger.info('Encolando generacion de resumen ejecutivo', 'GenerationService.generateExecutiveSummary');
    return this.api.post<GenerateEnqueueResponse>('documents/generate', payload);
  }

  downloadPdf(generateSummaryId: string): Observable<DownloadedFile> {
    this.frontendLogger.info('Descargando PDF del resumen generado', 'GenerationService.downloadPdf');
    return this.api.getBlobWithFilename(`documents/generated-summaries/${generateSummaryId}/pdf`);
  }

  // R3 C14: export DOCX (Business+, feature generated_documents_export_docx).
  downloadDocx(generateSummaryId: string): Observable<DownloadedFile> {
    this.frontendLogger.info('Descargando DOCX del resumen generado', 'GenerationService.downloadDocx');
    return this.api.getBlobWithFilename(`documents/generated-summaries/${generateSummaryId}/docx`);
  }

  listSummaries(limit = 100, lifecycleStatus?: GeneratedSummaryLifecycleStatus): Observable<GeneratedSummaryListItem[]> {
    this.frontendLogger.info('Listando historico de resumenes generados', 'GenerationService.listSummaries');
    const params: { limit: number; lifecycleStatus?: GeneratedSummaryLifecycleStatus } = { limit };
    if (lifecycleStatus) {
      params.lifecycleStatus = lifecycleStatus;
    }
    return this.api
      .get<GeneratedSummaryListResponse>('documents/generated-summaries', params)
      .pipe(map((res) => res.items ?? []));
  }

  /** R3.1 riesgo 5: detalle completo del resumen (contenido incluido). */
  getDetail(generateSummaryId: string): Observable<GeneratedSummaryDetail> {
    this.frontendLogger.info('Consultando detalle del resumen generado', 'GenerationService.getDetail');
    return this.api.get<GeneratedSummaryDetail>(`documents/generated-summaries/${generateSummaryId}`);
  }

  // ── R3 lifecycle: la identidad viaja por token; sin account_id/user_id en los requests ──

  submitReview(generateSummaryId: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Enviando resumen a revision', 'GenerationService.submitReview');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/submit-review`, {});
  }

  approve(generateSummaryId: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Aprobando resumen', 'GenerationService.approve');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/approve`, {});
  }

  reject(generateSummaryId: string, reason: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Rechazando resumen', 'GenerationService.reject');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/reject`, { reason: reason.trim() });
  }

  archive(generateSummaryId: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Archivando resumen', 'GenerationService.archive');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/archive`, {});
  }

  /** R3.1 G-1: devuelve un rechazado a borrador para corregirlo y reenviarlo. */
  revise(generateSummaryId: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Devolviendo resumen a borrador', 'GenerationService.revise');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/revise`, {});
  }

  /** R3.1 riesgo 4: restaura un archivado a borrador (solo aprobador; 403 al resto). */
  unarchive(generateSummaryId: string): Observable<SummaryLifecycleActionResponse> {
    this.frontendLogger.info('Restaurando resumen archivado', 'GenerationService.unarchive');
    return this.api.post<SummaryLifecycleActionResponse>(
      `documents/generated-summaries/${generateSummaryId}/unarchive`, {});
  }

  getTransitions(generateSummaryId: string): Observable<SummaryLifecycleTransition[]> {
    this.frontendLogger.info('Consultando timeline de transiciones', 'GenerationService.getTransitions');
    return this.api.get<{ items?: SummaryLifecycleTransition[] }>(
      `documents/generated-summaries/${generateSummaryId}/transitions`)
      .pipe(map((res) => res.items ?? []));
  }
}
